/**
 * Wabenflaggen in the client (amendment 13): one copy of the inbox, shared.
 *
 * The header counter, the cell sheet and the inbox panel all read and change the same state,
 * so an answer given in the panel is reflected in the counter without either knowing about
 * the other. The server is the authority — every action refreshes from it rather than
 * patching the local copy, because a hand-over supersedes other people's requests and
 * predicting that on the client would be a second implementation of the rule.
 *
 * Requests are sent directly rather than through the outbox. A request is a question to a
 * person about who holds something *now*; queueing it offline and delivering it on Tuesday to
 * whoever held the flag last week would be answering a question nobody is asking any more.
 * It still carries an opId, so a retry after a lost answer is replayed, not refused.
 */

import { requestState } from '$domain/flags.ts';
import { request } from './api.ts';
import type { FlagInbox } from './types.ts';

class Flags {
  inbox = $state<FlagInbox | undefined>();

  /** What the 🚩 in the header counts: requests to you that are still waiting for an answer. */
  readonly waiting = $derived.by(() => {
    const inbox = this.inbox;
    if (inbox === undefined) return 0;
    return inbox.incoming.filter(
      (entry) => requestState(entry.status, entry.week, inbox.currentWeek) === 'open',
    ).length;
  });

  async refresh(): Promise<void> {
    try {
      this.inbox = await request<FlagInbox>('/api/me/flags');
    } catch {
      // A stale count is better than none; the next refresh corrects it.
    }
  }

  async ask(cell: string, reason: string, note: string): Promise<void> {
    await request(`/api/cells/${cell}/flag/requests`, {
      method: 'POST',
      body: JSON.stringify({ reason, note: note.trim() === '' ? null : note, opId: crypto.randomUUID() }),
    });
    await this.refresh();
  }

  async answer(id: string, verb: 'accept' | 'decline' | 'report'): Promise<void> {
    await request(`/api/flag-requests/${id}/${verb}`, { method: 'POST', body: '{}' });
    await this.refresh();
  }
}

export const flags = new Flags();
